import React, { useMemo } from 'react';
import Dashboard from '../dashboard/Dashboard.tsx';
import { useTrending } from './hooks/useTrending.ts';
import { BrandSelector } from './components/BrandSelector.tsx';
import { TrendingSkeleton } from './components/TrendingSkeleton.tsx';
import {
  Box,
  Typography,
  Grid,
  Alert,
  Stack,
  Button
} from '@mui/joy';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { StyleCard } from '../../components/StyleCard.tsx';

// Custom Pagination Component
const PaginationControls = React.memo<{
  pagination: any;
  onPageChange: (cursor?: string) => void;
}>(({ pagination, onPageChange }) => {
  if (!pagination.hasMore && !pagination.currentCursor) return null;

  return (
    <Stack direction="row" justifyContent="center" spacing={2} alignItems="center" sx={{ mt: 3 }}>
      <Button
        variant="outlined"
        size="sm"
        onClick={() => onPageChange()}
        disabled={!pagination.currentCursor}
        startDecorator={<KeyboardArrowLeftIcon />}
      >
        Previous
      </Button>
      
      <Typography level="body-sm">
        {pagination.currentCursor ? 'Page ' : 'First page'} • {pagination.limit} items per page
        {pagination.hasMore && ' • More results available'}
      </Typography>
      
      <Button
        variant="outlined"
        size="sm"
        onClick={() => onPageChange(pagination.nextCursor)}
        disabled={!pagination.hasMore}
        endDecorator={<KeyboardArrowRightIcon />}
      >
        Next
      </Button>
    </Stack>
  );
});

PaginationControls.displayName = 'PaginationControls';

export const Trending = React.memo(() => {
  const {
    styles,
    selectedBrands,
    tempSelectedBrands,
    handleBrandToggle,
    handleSearch,
    handlePageChange,
    handleModalClose,
    clearSelectedBrands,
    clearAndCommit,
    resetTempSelections,
    isLoading,
    error,
    searchResults,
    pagination,
  } = useTrending();

  // Memoize the search results rendering to prevent unnecessary re-renders
  const searchResultsContent = useMemo(() => {
    if (isLoading) return <TrendingSkeleton />;

    if (error) {
      return (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    // Show results when we have search results, regardless of selectedBrands length
    // This allows showing all brands on page load (selectedBrands.length === 0)
    if (!isLoading && searchResults.length > 0) {
      return (
        <Box sx={{ mb: 4, px: 1.5, py: 1.5 }}>
          <Grid container spacing={3}>
            {searchResults.map((style, index) => (
              <StyleCard key={index} style={style} index={index} />
            ))}
          </Grid>
          
          <PaginationControls 
            pagination={pagination} 
            onPageChange={handlePageChange} 
          />
        </Box>
      );
    }

    // Show "no results" message only when specific brands are selected but no results found
    if (!isLoading && selectedBrands.length > 0 && searchResults.length === 0) {
      return (
        <Box sx={{ mb: 4 }}>
          <Alert color="neutral">
            No trending styles found. Try searching for different brands.
          </Alert>
        </Box>
      );
    }

    return null;
  }, [isLoading, error, selectedBrands.length, searchResults, pagination, handlePageChange]);

  return (
    <Dashboard>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 4 }}>
        <Typography
          level="h2" component="h1" style={{
            fontSize: "1.8em",
            fontWeight: 'bold',
            fontFamily: "Libre Baskerville"
          }}
        >
          Trending Styles
        </Typography>
        <Typography color="neutral" level="body-md">
          Track popular styles and market performance on Poshmark.
        </Typography>

        <Box sx={{ mt: 3, mb: 1 }}>
          <BrandSelector
            brands={styles}
            selectedBrands={selectedBrands}
            tempSelectedBrands={tempSelectedBrands}
            onBrandToggle={handleBrandToggle}
            onSearch={handleSearch}
            onModalClose={handleModalClose}
            onClearSelection={clearSelectedBrands}
            onClearAndCommit={clearAndCommit}
            onResetTempSelections={resetTempSelections}
            disabled={false}
            loading={isLoading}
          />
        </Box>

        {searchResultsContent}
      </Box>
    </Dashboard>
  );
});

Trending.displayName = 'Trending';
